/**
 * ISSUE-221 — renders WP-010 report envelope (structured metrics + alerts + narrative hook).
 */

import React, { useMemo } from 'react';
import { Alert, Card, Collapse, Descriptions, Space, Tag, Typography } from 'antd';
import type {
  ReportSnapshotAlert,
  ReportSnapshotEnvelope,
  ReportValueMetricBlock,
  Wp010ReportData,
} from '../../types/clientSnapshotReports';
import styles from './ReportSnapshotEnvelopeView.module.css';

const { Text, Paragraph } = Typography;

function isStructuredAlert(a: unknown): a is ReportSnapshotAlert {
  return (
    typeof a === 'object' &&
    a !== null &&
    ('message' in a || 'code' in a)
  );
}

function alertType(
  severity: ReportSnapshotAlert['severity']
): 'info' | 'warning' | 'error' | 'success' {
  if (severity === 'warning') return 'warning';
  if (severity === 'error') return 'error';
  return 'info';
}

function asWp010Data(data: ReportSnapshotEnvelope['data']): Wp010ReportData {
  return (data && typeof data === 'object' ? data : {}) as Wp010ReportData;
}

function MetricCell({ label, block }: { label: string; block?: ReportValueMetricBlock }) {
  if (!block || (block.score == null && block.ranking == null)) {
    return <Text type="secondary">—</Text>;
  }
  return (
    <Space size={4} wrap>
      <Tag>{label}</Tag>
      {block.score != null && <span>score {Number(block.score).toFixed(1)}</span>}
      {block.ranking != null && <span>rank #{block.ranking}</span>}
      {block.ratio != null && <span>ratio {Number(block.ratio).toFixed(2)}</span>}
      {block.trend && <Tag color="blue">{String(block.trend)}</Tag>}
    </Space>
  );
}

export interface ReportSnapshotEnvelopeViewProps {
  envelope: ReportSnapshotEnvelope;
  /** Card title; defaults to capitalized report_type */
  title?: string;
}

export const ReportSnapshotEnvelopeView: React.FC<ReportSnapshotEnvelopeViewProps> = ({
  envelope,
  title,
}) => {
  const data = asWp010Data(envelope.data);
  const metrics = data.metrics;
  const summary = data.summary;
  const headline =
    summary && typeof summary === 'object' && 'headline' in summary
      ? String((summary as { headline?: unknown }).headline ?? '')
      : '';

  const cardTitle =
    title ??
    `${String(envelope.report_type).charAt(0).toUpperCase()}${String(envelope.report_type).slice(1)} report`;

  const alerts = useMemo(() => {
    const raw = envelope.alerts ?? [];
    return raw.map((a, i) => {
      if (isStructuredAlert(a)) {
        const code = a.code != null && String(a.code).trim() !== '' ? String(a.code) : '';
        const message = a.message != null && String(a.message).trim() !== '' ? String(a.message) : '';
        if (code || message) {
          const msg = [code, message].filter(Boolean).join(' — ');
          return (
            <Alert
              key={`${code || 'alert'}-${i}`}
              type={alertType(a.severity)}
              showIcon
              message={msg}
              className={styles.alert}
            />
          );
        }
        return (
          <Alert
            key={`empty-structured-${i}`}
            type="info"
            showIcon
            message={JSON.stringify(a)}
            className={styles.alert}
          />
        );
      }
      return (
        <Alert key={i} type="info" showIcon message={String(a)} className={styles.alert} />
      );
    });
  }, [envelope.alerts]);

  const stubTag = data.stub === true ? <Tag color="orange">stub data</Tag> : null;
  const versionTag =
    data.schema_version != null ? (
      <Tag color="processing">schema {String(data.schema_version)}</Tag>
    ) : null;

  return (
    <Card
      title={
        <Space wrap>
          <span>{cardTitle}</span>
          {versionTag}
          {stubTag}
        </Space>
      }
      size="small"
      className={styles.card}
    >
      <Text type="secondary" className={styles.meta}>
        Generated {envelope.generated_at} · Company {envelope.company_id}
        {data.company_name != null && data.company_name !== '' && (
          <> · {data.company_name}</>
        )}
      </Text>

      {alerts.length > 0 && <div className={styles.alertBlock}>{alerts}</div>}

      {headline !== '' && (
        <Paragraph strong className={styles.headline}>
          {headline}
        </Paragraph>
      )}

      {metrics && (
        <Descriptions bordered size="small" column={1} className={styles.desc}>
          {metrics.systemic_index != null && (
            <Descriptions.Item label="Systemic index">
              {Number(metrics.systemic_index).toFixed(1)}
            </Descriptions.Item>
          )}
          {metrics.recommended_focus != null && metrics.recommended_focus !== '' && (
            <Descriptions.Item label="Recommended focus">
              {String(metrics.recommended_focus)}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Customer value">
            <MetricCell label="CV" block={metrics.customer_value} />
          </Descriptions.Item>
          <Descriptions.Item label="Business value">
            <MetricCell label="BV" block={metrics.business_value} />
          </Descriptions.Item>
          <Descriptions.Item label="Stakeholder value">
            <MetricCell label="SV" block={metrics.stakeholder_value} />
          </Descriptions.Item>
          {metrics.message != null && metrics.message !== '' && (
            <Descriptions.Item label="Metrics note">{metrics.message}</Descriptions.Item>
          )}
        </Descriptions>
      )}

      {envelope.narrative_prompt != null && envelope.narrative_prompt !== '' && (
        <Paragraph className={styles.narrative}>
          <Text strong>Narrative prompt (KRYBOT)</Text>: {envelope.narrative_prompt}
        </Paragraph>
      )}

      <Collapse
        size="small"
        items={[
          {
            key: 'raw',
            label: 'Technical payload',
            children: (
              <pre className={styles.pre}>{JSON.stringify(envelope.data ?? {}, null, 2)}</pre>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default ReportSnapshotEnvelopeView;
